import type { ReactNode } from "react";
import { cn } from "@/lib/utils.ts";

/** A titled block of the right panel. */
export function PanelSection({
  title,
  aside,
  children,
  className,
}: {
  title: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("flex flex-col gap-2.5", className)}>
      <div className="flex min-h-6 items-center justify-between gap-2">
        <h3 className="text-[11.5px] font-bold tracking-[0.06em] text-muted-foreground uppercase">{title}</h3>
        {aside}
      </div>
      {children}
    </section>
  );
}

/** Confidence as a blue meter with the percentage. */
export function ConfidenceMeter({ value, label, compact = false }: { value: number; label: string; compact?: boolean }) {
  const clamped = Math.max(0, Math.min(1, value));
  const percent = Math.round(clamped * 100);
  return (
    <div className={cn("flex items-center gap-2.5", compact ? "w-full max-w-40" : "w-full")}>
      {!compact ? <span className="shrink-0 text-[12px] font-medium text-muted-foreground">{label}</span> : null}
      <div
        role="meter"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        className={cn("relative flex-1 overflow-hidden rounded-full bg-meter-track", compact ? "h-1.5" : "h-2")}
      >
        <span className="absolute inset-y-0 left-0 rounded-full bg-meter-fill" style={{ width: `${percent}%` }} />
      </div>
      <span className={cn("code-mono shrink-0 font-semibold text-foreground", compact ? "text-[11.5px]" : "text-[12.5px]")}>{percent}%</span>
    </div>
  );
}
